/**
 * The arithmetic behind the world map, apart from the drawing so it can be
 * tested by the plain node runner (which cannot load `.tsx`).
 */

/** How strongly a country is tinted, 0 (no traffic) to 1 (the busiest one). */
export function mapIntensity(value: number | undefined, max: number): number {
  if (!value || value <= 0 || max <= 0) return 0;
  // Square-root so a country with a tenth of the leader's clicks still
  // reads as coloured rather than vanishing into the land tone: the map is
  // for spotting *where* traffic comes from, and a linear scale showed one
  // dark country and a hundred white ones.
  return Math.max(0.15, Math.min(1, Math.sqrt(value / max)));
}

/**
 * Folds the report's keys down to the map's: `US-NJ` counts for `US`, a
 * lowercase or padded code still finds its country, and anything that is not
 * a country (an unknown origin) is left out rather than drawn somewhere.
 */
export function countryTotals(rows: Iterable<readonly [string | null | undefined, number]>): Map<string, number> {
  const totals = new Map<string, number>();
  for (const [key, n] of rows) {
    const iso = (key ?? '').trim().toUpperCase().slice(0, 2);
    if (!/^[A-Z]{2}$/.test(iso)) continue;
    totals.set(iso, (totals.get(iso) ?? 0) + n);
  }
  return totals;
}

export interface MapViewport {
  readonly x: number;
  readonly y: number;
  readonly scale: number;
}

export const MAP_MIN_SCALE = 1;
export const MAP_MAX_SCALE = 6;

/** Zooms about the centre of what is on screen, clamped to the canvas. */
export function zoomViewport(
  v: MapViewport, factor: number, width: number, height: number,
): MapViewport {
  const scale = Math.min(MAP_MAX_SCALE, Math.max(MAP_MIN_SCALE, v.scale * factor));
  const cx = v.x + width / v.scale / 2;
  const cy = v.y + height / v.scale / 2;
  return clampViewport({ x: cx - width / scale / 2, y: cy - height / scale / 2, scale }, width, height);
}

/** Moves the window by a drag measured in screen pixels. */
export function panViewport(
  v: MapViewport, dxPx: number, dyPx: number, width: number, height: number, drawnWidth: number,
): MapViewport {
  const unitsPerPx = width / drawnWidth / v.scale;
  return clampViewport({ x: v.x - dxPx * unitsPerPx, y: v.y - dyPx * unitsPerPx, scale: v.scale }, width, height);
}

export function clampViewport(v: MapViewport, width: number, height: number): MapViewport {
  const w = width / v.scale;
  const h = height / v.scale;
  return {
    scale: v.scale,
    x: Math.min(Math.max(0, v.x), Math.max(0, width - w)),
    y: Math.min(Math.max(0, v.y), Math.max(0, height - h)),
  };
}

export function viewBoxOf(v: MapViewport, width: number, height: number): string {
  return `${v.x.toFixed(1)} ${v.y.toFixed(1)} ${(width / v.scale).toFixed(1)} ${(height / v.scale).toFixed(1)}`;
}
